import {
    DeviceIdService,
    HttpService,
    PulseResponse,
    TrafficSource,
    TrafficSourceService,
} from '@pulse/browser-shared';
import { ExperimentInfo, SettingsAttributes, SettingsConfigurations } from '@pulse/common';
import {
    DEFAULT_DEBUG_EXPERIMENT,
    DEFAULT_TRAFFIC_SOURCE,
    FirstCall,
    SettingsResponse,
} from '../../models';
import { ExperimentDebugService } from '../experiment-debug.service';
import { ExperimentsService } from '../experiments.service';
import { ResponseTrackerService } from '../response-tracker.service';

interface ComposeRequestURLParams {
    // App and version related
    app: string;
    app_version: string;
    tag: string;
    settingsBaseURL: string;

    // User identification related
    deviceId: string;
    userId: string | undefined | null;
    tierId: string | undefined | null;
    deviceLastTierId: string | undefined | null;
    userRole: string | null;
    isTest: string | null;

    // Experiment related
    experiments: string;
    debugExperiment: ExperimentInfo;

    // Traffic source related
    trafficSource: TrafficSource;

    // Custom attributes
    attributes: SettingsAttributes;
}

export class SettingsFetchHelper {
    constructor(
        private responseTrackerService: ResponseTrackerService,
        private deviceIdService: DeviceIdService,
        private experimentsService: ExperimentsService,
        private experimentDebugService: ExperimentDebugService,
        private trafficSourceService: TrafficSourceService,
        private config: SettingsConfigurations,
        private httpService: HttpService,
    ) {}

    private composeRequestURL({
        // App and version related
        app,
        app_version,
        tag,
        settingsBaseURL,

        // User identification related
        deviceId,
        userId,
        tierId,
        deviceLastTierId,
        userRole,
        isTest,

        // Experiment related
        experiments,
        debugExperiment,

        // Traffic source related
        trafficSource,

        // Custom attributes
        attributes,
    }: ComposeRequestURLParams): URL {
        const url = new URL(`${settingsBaseURL}/${app}/${tag}`);

        // Set device and user identification parameters
        url.searchParams.set('did', deviceId);
        if (userId) {
            url.searchParams.set('uid', userId);
        }
        if (tierId) {
            url.searchParams.set('tid', tierId);
        }
        if (deviceLastTierId) {
            url.searchParams.set('dltid', deviceLastTierId);
        }
        if (userRole) {
            url.searchParams.set('ur', userRole);
        }
        if (isTest) {
            url.searchParams.set('is-test', isTest);
        }

        // Set app version if provided and not default
        if (app_version && app_version !== '0') {
            url.searchParams.set('versioncode', app_version);
        }

        // Set experiment parameters
        if (experiments) {
            url.searchParams.set('exp', experiments);
        }
        if (debugExperiment && debugExperiment !== DEFAULT_DEBUG_EXPERIMENT) {
            url.searchParams.set('debug-exp', `${debugExperiment.id}:${debugExperiment.variant}`);
        }

        // Set traffic source parameters
        if (trafficSource?.referrer && trafficSource !== DEFAULT_TRAFFIC_SOURCE) {
            url.searchParams.set('ref', trafficSource.referrer.url);
        }
        if (trafficSource?.clickId && trafficSource !== DEFAULT_TRAFFIC_SOURCE) {
            url.searchParams.set('cidp', trafficSource.clickId.source);
        }
        if (trafficSource?.utmParams && trafficSource !== DEFAULT_TRAFFIC_SOURCE) {
            const { utm_source, utm_medium, utm_campaign, utm_content } = trafficSource.utmParams;

            if (utm_source) {
                url.searchParams.set('usrc', utm_source);
            }
            if (utm_medium) {
                url.searchParams.set('umed', utm_medium);
            }
            if (utm_campaign) {
                url.searchParams.set('ucmp', utm_campaign);
            }
            if (utm_content) {
                url.searchParams.set('ucnt', utm_content);
            }
        }

        // Set custom attributes with limit check
        const MAX_ATTRIBUTES = 10;
        const attributesEntries = Object.entries(attributes);

        if (attributesEntries.length > 0 && attributesEntries.length <= MAX_ATTRIBUTES) {
            for (const [key, value] of attributesEntries) {
                url.searchParams.set(`attr-${key}`, value);
            }
        }

        return url;
    }

    handleResponse(
        tag: string,
        res: PulseResponse<SettingsResponse>,
        resolve: (
            value: PulseResponse<SettingsResponse> | PromiseLike<PulseResponse<SettingsResponse>>,
        ) => void,
        settingName: string,
        emptyForTags: string[],
        mutateFirstCallState: (prop: keyof FirstCall, value: boolean, state: FirstCall) => void,
        firstCallState: FirstCall,
        attributesHash: string,
    ) {
        resolve(res);
        this.cacheResponse(
            res,
            tag,
            settingName,
            emptyForTags,
            mutateFirstCallState,
            firstCallState,
            attributesHash,
        );
    }

    async cacheResponse(
        res: PulseResponse<SettingsResponse> | Response,
        tag: string,
        settingName: string,
        emptyForTags: string[],
        mutateFirstCallState: (prop: keyof FirstCall, value: boolean, state: FirstCall) => void,
        firstCallState: FirstCall,
        attributesHash: string,
    ) {
        let body;

        if (res.body instanceof ReadableStream && res instanceof Response) {
            body = await res.clone().json();
        } else {
            body = res.body;
        }

        const trackData = {
            settings: body.settings,
            experiments: body.experiments,
            segments: body.segments,
            tag,
            emptyForTags,
            attributesHash,
        };

        // Track response if request succeeded, to cache the data in storage
        if (res && typeof res === 'object' && res?.status === 200 && body?.settings) {
            if (!firstCallState.firstCallDone) {
                mutateFirstCallState('firstCallDone', true, firstCallState);
                mutateFirstCallState('defaultReturned', false, firstCallState);
            }

            // Add empty value if setting is not in resopnse
            if (
                typeof trackData.settings === 'object' &&
                trackData.settings !== null &&
                !(settingName in trackData.settings)
            ) {
                emptyForTags.push(tag);
                trackData.settings[settingName] = undefined;
            }
            this.responseTrackerService.trackResponse(trackData);
        }
    }

    createFakeResponse(
        tag: string,
        firstCallState: FirstCall,
        mutateFirstCallState: (prop: keyof FirstCall, value: boolean, state: FirstCall) => void,
        attributesHash: string,
    ): PulseResponse<SettingsResponse> {
        if (!firstCallState.firstCallDone) {
            mutateFirstCallState('firstCallDone', true, firstCallState);
            mutateFirstCallState('defaultReturned', true, firstCallState);
        }

        return {
            ok: false,
            body: {
                settings: {},
                experiments: [],
                segments: [],
                tag,
                emptyForTags: [],
                attributesHash,
            },
            headers: new Map([]),
            status: null,
        };
    }

    async fetchSettings(
        tag: string,
        userRole: string | null,
        isTest: string | null,
        settingName: string,
        emptyForTags: string[],
        attributes: SettingsAttributes,
        config: SettingsConfigurations,
        userId: string | number | undefined | null,
        tierId: string | number | undefined | null,
        deviceLastTierId: string | number | undefined | null,
        firstCallState: FirstCall,
        mutateFirstCallState: (prop: keyof FirstCall, value: boolean, state: FirstCall) => void,
        attributesHash: string,
    ): Promise<PulseResponse<SettingsResponse>> {
        if (!config.app) {
            throw new Error('Application name has not been provided to settings SDK!');
        }

        const url = this.composeRequestURL({
            tag,
            userRole,
            isTest,
            attributes,
            app: config.app,
            app_version: config.app_version || '0',
            deviceId: this.deviceIdService.getDeviceId(),
            userId: userId?.toString() || null,
            tierId: tierId?.toString() || null,
            deviceLastTierId: deviceLastTierId?.toString() || null,
            experiments: this.experimentsService.getExperimentsString(),
            debugExperiment:
                this.experimentDebugService.getDebugExperiment() || DEFAULT_DEBUG_EXPERIMENT,
            trafficSource:
                this.trafficSourceService.getLastTrafficSource() || DEFAULT_TRAFFIC_SOURCE,
            settingsBaseURL: this.config.settingsBaseURL,
        });

        return new Promise((resolve) => {
            const timeoutId = setTimeout(
                () =>
                    resolve(
                        this.createFakeResponse(
                            tag,
                            firstCallState,
                            mutateFirstCallState,
                            attributesHash,
                        ),
                    ),
                this.config.timeout,
            );

            const headers: Record<string, string> = {};
            this.httpService
                .get<SettingsResponse>(url.href, headers, 'high')
                .then((res) => {
                    clearTimeout(timeoutId);
                    this.handleResponse(
                        tag,
                        res,
                        resolve,
                        settingName,
                        emptyForTags,
                        mutateFirstCallState,
                        firstCallState,
                        attributesHash,
                    );
                })
                .catch(() => {
                    clearTimeout(timeoutId);
                    resolve(
                        this.createFakeResponse(
                            tag,
                            firstCallState,
                            mutateFirstCallState,
                            attributesHash,
                        ),
                    );
                });
        });
    }
}
